import React, { useState, useEffect, useContext, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Button,
  Box,
  Paper,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import TextField from '@mui/material/TextField';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Customers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function EditGeneralEnquiry() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [groupCodes, setGroupCodes] = useState([]);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [leadData, setLeadData] = useState(null);
  const [editorData, setEditorData] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [modalShow, setModalShow] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      group_code: '',
      lender_panel_id: '',
      details: '',
      override_date: null
    }
  });

  useEffect(() => {
    if (authCtx.token && lead_id) {
      fetchGroupCodes();
      fetchLenderPanels();
      fetchLeadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead_id, authCtx.token]);

  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setGroupCodes(res.data);
      }
    } catch (err) {
      console.error('Error fetching group codes:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  const fetchLeadData = async () => {
    setLoading(true);
    try {
      const response = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);
      const lead = response?.data;

      if (lead) {
        setLeadData(lead);
        setSelectedCustomers(lead.customers || []);
        setEditorData(lead.details || '');

        // Handle both populated objects and ObjectId strings
        const groupCodeValue = lead.group_code
          ? (typeof lead.group_code === 'object' ? String(lead.group_code._id) : String(lead.group_code))
          : '';

        const lenderPanelValue = lead.lender_panel_id
          ? (typeof lead.lender_panel_id === 'object' ? String(lead.lender_panel_id._id) : String(lead.lender_panel_id))
          : '';

        const formValues = {
          group_code: groupCodeValue,
          lender_panel_id: lenderPanelValue,
          details: lead.details || '',
          override_date: lead.override_date ? dayjs(lead.override_date) : null
        };

        reset(formValues);
      }
    } catch (err) {
      console.error('Error fetching lead:', err);
      Swal.fire('Error', 'Failed to load lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerAdd = (customer) => {
    const isAlreadyAdded = selectedCustomers.some(c => c._id === customer._id);
    if (!isAlreadyAdded) {
      setSelectedCustomers([...selectedCustomers, customer]);
    }
  };

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const payload = {
        lead_id: leadData.lead_id,
        group_code: formData.group_code,
        lender_panel_id: formData.lender_panel_id || null,
        details: editorData,
        override_date: formData.override_date ? formData.override_date.toISOString() : null,
        customers: selectedCustomers.map(c => c._id)
      };

      const response = await postRequest(`private/update-general-enquiry/${lead_id}`, payload, authCtx.token);

      if (response?.status === 200 || response?.status === 'success') {
        Swal.fire({
          title: 'Success!',
          text: 'General Enquiry updated successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          navigate(`/leads/view/general-enquiry/${leadData.lead_id}`);
        });
      } else {
        Swal.fire('Error', response?.message || 'Update failed', 'error');
      }
    } catch (err) {
      console.error('Error updating lead:', err);
      Swal.fire('Error', 'An error occurred while updating', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!authCtx.token || loading || !leadData || groupCodes.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit General Enquiry', path: '' }
        ]}
      />
      <br />

      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Edit General Enquiry #{leadData?.lead_id}</h1>
      </div>

      <div className='page-top-formview mb-3'>
        <Paper elevation={3} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              {/* Group Code */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Group Code *</Form.Label>
                  <Controller
                    name="group_code"
                    control={control}
                    rules={{ required: 'Group code is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.group_code}>
                        <Select {...field} displayEmpty>
                          <MenuItem value="">
                            <em>Select Group Code</em>
                          </MenuItem>
                          {groupCodes.map((gc) => (
                            <MenuItem key={gc._id} value={gc._id}>
                              {gc.code} - {gc.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.group_code && (
                          <span className="text-danger">{errors.group_code.message}</span>
                        )}
                      </FormControl>
                    )}
                  />
                </Form.Group>
              </Col>

              {/* Lender Panel */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Lender Panel</Form.Label>
                  <Controller
                    name="lender_panel_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <Select {...field} displayEmpty>
                          <MenuItem value="">
                            <em>Select Lender Panel (Optional)</em>
                          </MenuItem>
                          {lenderPanels.map((panel) => (
                            <MenuItem key={panel._id} value={panel._id}>
                              {panel.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              {/* Override Date */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Override Date</Form.Label>
                  <Controller
                    name="override_date"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          {...field}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: { fullWidth: true, variant: 'outlined' }
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              {/* Details */}
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Details</Form.Label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={editorData}
                    onChange={(_event, editor) => {
                      const data = editor.getData();
                      setEditorData(data);
                    }}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'blockQuote',
                        'undo',
                        'redo'
                      ]
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Customers Section */}
            <Row className="mb-3 mt-3">
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedCustomers.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedCustomers(prev =>
                          prev.filter(c => c._id !== customer._id)
                        )
                      }
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setModalShow(true)}
                  sx={{ color: '#6f42c1', textTransform: 'none' }}
                >
                  Add Customer
                </Button>
              </Box>
              <CustomerSearchModal
                show={modalShow}
                onHide={() => setModalShow(false)}
                authToken={authCtx.token}
                onCustomerAdd={handleCustomerAdd}
              />
            </Row>

            <Row className="m-0">
              <Stack spacing={2} direction="row" sx={{ justifyContent: 'flex-start' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    textTransform: 'capitalize',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                    }
                  }}
                >
                  {loading ? 'Updating...' : 'Update Lead'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/leads/view/general-enquiry/${leadData?.lead_id}`)}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    textTransform: 'capitalize'
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Row>
          </form>
        </Paper>
      </div>
    </>
  );
}
